import React, { useMemo, useState } from 'react';
import { Column, Row } from '@shared/ui/atoms/StructuralLayout';
import TeamDropDownList from '@shared/ui/organisms/team-drop-down-list/TeamDropDownList';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { TeamWithCount } from '@shared/models/team.models';
import { UserInfo } from "@shared/models/user.models";
import { useDispatch, useSelector } from "react-redux";
import { selectIsEditUserLoading, setEditingUser } from "@features/settings/store/editUser.duck";
import { selectCurrentUser, updateCurrentUser } from "@shared/store/auth.duck";
import { UpdateUserField, updateUserField } from '@features/settings/services/UserService';
import { successNotification, warningNotification } from "@shared/store/notification.duck";
import { canUserEditTeamId } from "@shared/utils/AccessControl";

export function ChangeUsersTeamDropDownComponent(props: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const {teams, editingUser} = props;
  const isEditUserLoading = useSelector(selectIsEditUserLoading);
  const [requestProcessing, setRequestProcessing] = useState(false);
  const canEditTeam = canUserEditTeamId(currentUser, editingUser.teamId);

  const isLoading = useMemo(() => isEditUserLoading || requestProcessing, [isEditUserLoading, requestProcessing]);

  const onTeamSelected = async (teamId: string) => {
    if (isLoading) return;

    try {
      setRequestProcessing(true);
      await updateUserField(editingUser.userId, UpdateUserField.team, teamId);
      dispatch(setEditingUser({
        ...editingUser,
        teamId: teamId,
      }));
      // If user is editing themselves refresh current user.
      if (editingUser.userId === currentUser?.id) {
        dispatch(updateCurrentUser(true));
      }

      dispatch(successNotification('Users team updated'));
    } catch (e) {
      dispatch(warningNotification('Failed to update users team'));
    } finally {
      setRequestProcessing(false);
    }
  }

  return (
    <Row>
      <Column style={{flex: 1, marginRight: 8}}>
        <TeamDropDownList disabled={isLoading || !canEditTeam}
                          label="settings-page.edit-user.member-of-team"
                          onSelect={(teamId: string) => onTeamSelected(teamId)}
                          teams={teams}
                          selectedTeamId={editingUser.teamId} />
      </Column>
      {isLoading && <SimpleSpinner style={{marginBottom: -12}} size={22} />}
    </Row>
  )
}

interface Props {
  teams: TeamWithCount[];
  editingUser: UserInfo;
}
